import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// La session vit dans un cookie HttpOnly posé par l'API : le JS n'y touche jamais.
const api = axios.create({ baseURL: API, withCredentials: true });

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then(r => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Demande un lien de connexion. Aucun compte n'est créé à cette étape.
  const requestLink = useCallback(email => api.post("/auth/magic-link", { email }).then(r => r.data), []);

  // Consomme le lien : ouvre la session et crée le compte au premier usage.
  const verifyLink = useCallback(async token => {
    const r = await api.post("/auth/verify", { token });
    setUser(r.data);
    return r.data;
  }, []);

  const updateProfile = useCallback(async patch => {
    const r = await api.patch("/me", patch);
    setUser(r.data);
    return r.data;
  }, []);

  const uploadAvatar = useCallback(async dataUrl => {
    const r = await api.put("/me/avatar", { data: dataUrl });
    setUser(u => (u ? { ...u, avatar_url: r.data.avatar_url } : u));
    return r.data.avatar_url;
  }, []);

  const removeAvatar = useCallback(async () => {
    await api.delete("/me/avatar");
    setUser(u => (u ? { ...u, avatar_url: null } : u));
  }, []);

  const completeStep = useCallback(key => api.post("/me/steps", { key }).then(r => r.data), []);
  const fetchSteps = useCallback(() => api.get("/me/steps").then(r => r.data), []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* La session locale est effacée quoi qu'il arrive. */ }
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, requestLink, verifyLink, updateProfile, uploadAvatar, removeAvatar, completeStep, fetchSteps, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { API, api };

// Message d'erreur lisible, quelle que soit la forme renvoyée par l'API.
export function errorMessage(err, fallback = "Une erreur est survenue. Réessayez.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg).join(". ");
  return fallback;
}
